/* Conditional prices are explicit verified amounts, never inferred discount stacks. */
(function (scope) {
  'use strict';
  const MAX_AGE = 6 * 60 * 60 * 1000;
  const subscriptionTypes = ['SUBSCRIBE_AND_SAVE', 'SUBSCRIBEAND_SAVE', 'SUBSCRIBEANDSAVE'];
  const cents = value => typeof value === 'string' && /^\d{1,7}(?:\.\d{1,2})?$/.test(value)
    ? Math.round(Number(value) * 100) : null;
  const fresh = (start, end, now, maxAge) => {
    start = Date.parse(start); end = Date.parse(end);
    return Number.isFinite(start) && Number.isFinite(end) && start <= now + 60000 &&
      end > now && end > start && end - start <= maxAge && now - start < maxAge;
  };
  const amazonAsin = url => {
    try {
      const parsed = new URL(url);
      if (parsed.protocol !== 'https:' || !['amazon.com', 'www.amazon.com'].includes(parsed.hostname)) return null;
      return parsed.pathname.match(/^\/(?:dp|gp\/product)\/([A-Z0-9]{10})(?:\/|$)/)?.[1] || null;
    } catch { return null; }
  };
  function resolve(product, data, now = Date.now(), options = {}) {
    const base = cents(product?.price);
    if (!Number.isFinite(now) || !base || !/^[A-Z0-9]{10}$/.test(product?.asin || '') ||
      !['IN_STOCK', 'IN_STOCK_SCARCE', 'INSTOCK', 'INSTOCKSCARCE'].includes(product.availabilityType) ||
      !fresh(product.fetchedAt, product.expiresAt, now, 86400000) || amazonAsin(product.affiliateUrl) !== product.asin) return null;
    if (product.deal?.startTime && !(Date.parse(product.deal.startTime) <= now)) return null;
    if (product.deal?.endTime && !(Date.parse(product.deal.endTime) > now)) return null;
    const access = product.deal?.accessType;
    if (access && !['ALL', 'PRIME_EXCLUSIVE'].includes(access)) return null;
    // An API subscription price is already conditional; never subtract again.
    if (subscriptionTypes.includes(product.offerType)) return options.withoutSubscription ? null : {
      price: product.price, basePrice: null, subscription: true, coupon: null,
      primeRequired: access === 'PRIME_EXCLUSIVE', firstDeliveryOnly: false,
      checkedAt: product.fetchedAt, expiresAt: product.expiresAt, optionLabel: product.optionLabel || '', terms: '', source: 'api'
    };
    if (/SUBSCRIBE|SUBSCRIPTION/i.test(product.offerType || '')) return null;
    if (data?.version !== 1 || !Array.isArray(data.offers)) return null;
    const candidates = data.offers.slice(0, 30).filter(offer => {
      if (!offer || offer.asin !== product.asin || offer.currency !== 'USD' ||
        cents(offer.basePrice) !== base || !offer.merchant || offer.merchant !== product.merchant ||
        amazonAsin(offer.sourceUrl) !== product.asin || offer.finalPriceVerified !== true ||
        !fresh(offer.checkedAt, offer.expiresAt, now, MAX_AGE)) return false;
      const amount = cents(offer.finalPrice);
      if (!amount || amount >= base) return false;
      if (offer.priceBasis && !['displayed', 'confirmed_components'].includes(offer.priceBasis)) return false;
      if (offer.priceBasis === 'confirmed_components') {
        const subscriptionPrice = cents(offer.subscriptionPrice), discount = offer.coupon?.discount;
        const couponAmount = cents(String(discount?.value));
        if (!offer.subscription || !subscriptionPrice || subscriptionPrice >= base ||
          offer.stackingVerified !== true || discount?.type !== 'amount' ||
          !couponAmount || subscriptionPrice - couponAmount !== amount) return false;
      }
      if (typeof offer.subscription !== 'boolean' || typeof offer.primeRequired !== 'boolean' ||
        typeof offer.firstDeliveryOnly !== 'boolean' || typeof offer.terms !== 'string' || offer.terms.length > 700) return false;
      if (!offer.subscription && !offer.coupon) return false;
      if (offer.firstDeliveryOnly && !offer.subscription) return false;
      if (access === 'PRIME_EXCLUSIVE' && !offer.primeRequired) return false;
      if (offer.coupon) {
        if (!['clip', 'automatic'].includes(offer.coupon.application) ||
          typeof offer.coupon.requiresSubscription !== 'boolean' ||
          (offer.coupon.requiresSubscription && !offer.subscription)) return false;
        if (offer.subscription && (offer.stackingVerified !== true || !offer.firstDeliveryOnly)) return false;
        // Optional discount amounts are display data, never calculation instructions.
        if (offer.coupon.discount) {
          const discount = offer.coupon.discount;
          if (!['percent', 'amount'].includes(discount.type) ||
            !(typeof discount.value === 'number' && Number.isFinite(discount.value) && discount.value > 0) ||
            (discount.type === 'percent' ? discount.value >= 100 : !cents(String(discount.value)) || cents(String(discount.value)) >= base)) return false;
        }
      }
      return !options.withoutSubscription || !offer.subscription;
    }).sort((a, b) => cents(a.finalPrice) - cents(b.finalPrice));
    const offer = candidates[0];
    return offer ? {
      price: offer.finalPrice, basePrice: product.price, subscription: offer.subscription,
      coupon: offer.coupon || null, primeRequired: offer.primeRequired,
      firstDeliveryOnly: offer.firstDeliveryOnly, checkedAt: offer.checkedAt, expiresAt: offer.expiresAt,
      optionLabel: product.optionLabel || '', terms: offer.terms, source: 'verified',
      estimated: offer.priceBasis === 'confirmed_components'
    } : null;
  }
  function copyFor(root) {
    const copy = root.querySelector('[data-offer-copy]').content;
    return (key, values = {}) => {
      let value = copy.querySelector('[data-offer-label="' + key + '"]').textContent;
      Object.entries(values).forEach(([token, replacement]) => { value = value.replace('[' + token + ']', replacement); });
      return value;
    };
  }
  const money = value => new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: 'USD' }).format(Number(value));
  function qualifier(offer, text) {
    let value = text(offer.coupon ? (offer.subscription ? 'after_both' : 'after_coupon') : 'with_subscription');
    if (offer.firstDeliveryOnly) value += ' · ' + text('first_delivery');
    if (offer.primeRequired) value += ' · ' + text('prime');
    return value;
  }
  function renderCoupon(root, offer, text) {
    const row = root.querySelector('[data-coupon-row]');
    if (!row) return;
    row.hidden = !offer?.coupon;
    const badge = row.querySelector('[data-coupon-badge]');
    const note = row.querySelector('[data-coupon-note]');
    badge.textContent = '';
    if (note) note.textContent = '';
    if (!offer?.coupon) return;
    const discount = offer.coupon.discount;
    badge.textContent = text(discount ? 'coupon_badge_amount' : 'coupon_badge', {
      discount: discount ? (discount.type === 'percent' ? discount.value + '%' : money(discount.value)) : ''
    });
    if (note) note.textContent = text(offer.coupon.application === 'automatic' ? 'coupon_checkout' :
      (offer.coupon.requiresSubscription ? 'coupon_clip_subscription' : 'coupon_clip_amazon'));
  }
  function render(root, offer, alternative = null) {
    const find = selector => root.querySelector(selector);
    const panel = find('[data-offer-steps]'), condition = find('[data-offer-condition]');
    const base = find('[data-offer-base]');
    if (!panel || !condition || !base) return;
    root.dataset.conditionalPrice = String(Boolean(offer));
    panel.hidden = condition.hidden = base.hidden = !offer;
    find('[data-offer-alternative]').hidden = true;
    const estimated = find('[data-offer-estimate]'); estimated.hidden = !offer?.estimated;
    renderCoupon(root, offer, offer ? copyFor(root) : null);
    if (!offer) return;
    const text = copyFor(root);
    condition.textContent = qualifier(offer, text);
    base.hidden = !offer.basePrice;
    base.textContent = offer.basePrice ? text('one_time', { price: money(offer.basePrice) }) : '';
    const other = find('[data-offer-alternative]');
    other.hidden = !alternative || !offer.subscription;
    other.textContent = alternative && offer.subscription ? text(alternative.estimated ? 'alternative_estimated' : 'alternative_coupon', { price: money(alternative.price) }) + (alternative.primeRequired ? ' · ' + text('prime') : '') : '';
    const list = find('[data-offer-step-list]'); list.replaceChildren();
    const step = value => { const li = document.createElement('li'); li.textContent = value; list.append(li); };
    if (offer.primeRequired) step(text('sign_in_prime'));
    step(text(offer.subscription ? 'subscribe_step' : 'one_time_step'));
    if (offer.coupon) {
      const discount = offer.coupon.discount;
      let key = offer.coupon.application === 'automatic' ? 'automatic_coupon' : 'clip_coupon';
      if (discount && offer.coupon.application === 'clip') key = 'clip_discount';
      step(text(key, { discount: discount ? (discount.type === 'percent' ? discount.value + '%' : money(discount.value)) : '' }));
    }
    step(text(offer.estimated ? 'confirm_estimate' : 'confirm', { price: money(offer.price) }));
    find('[data-offer-option]').textContent = offer.optionLabel ? text('selected_option', { option: offer.optionLabel }) : '';
    find('[data-offer-option]').hidden = !offer.optionLabel;
    find('[data-offer-eligibility]').textContent = text('eligibility');
    find('[data-offer-recurring]').hidden = !offer.subscription;
    find('[data-offer-recurring]').textContent = text('recurring');
    const stamp = window.BizedealsTime.format(offer.checkedAt);
    find('[data-offer-verified-time]').textContent = text('checked', { time: stamp });
    find('[data-offer-verified-time]').dateTime = offer.checkedAt;
    find('[data-offer-extra-terms]').textContent = offer.terms;
    find('[data-offer-extra-terms]').hidden = !offer.terms;
  }
  function discountSummary(offer,reference) {
    const current=cents(offer?.price),basis=cents(reference?.amount);
    if(!offer||offer.estimated||!current||!basis||basis<=current)return null;
    const percent=Math.round((basis-current)/basis*100);
    return percent>0&&percent<100?{percent,amount:((basis-current)/100).toFixed(2)}:null;
  }
  function renderCard(root, offer) {
    const panel = root.querySelector('[data-card-conditional]');
    if (!panel) return;
    panel.hidden = !offer;
    root.dataset.conditionalPrice = String(Boolean(offer));
    renderCoupon(panel, offer, offer ? copyFor(root) : null);
    const discountBadge=panel.querySelector('[data-card-discount]');
    let snapshot;try{snapshot=JSON.parse(root.dataset.snapshot);}catch{}
    const reduction=discountSummary(offer,snapshot?.reference);
    if(discountBadge){discountBadge.hidden=!reduction;discountBadge.textContent=reduction?reduction.percent+'% off':'';}
    if (!offer) return;
    const text = copyFor(root);
    const estimated = panel.querySelector('[data-card-estimate]');
    estimated.hidden = !offer.estimated;
    const amount = panel.querySelector('.bd-money');
    const parts = Number(offer.price).toFixed(2).split('.');
    amount.querySelector('[data-money-full]').textContent = money(offer.price);
    amount.querySelector('[data-money-whole]').textContent = parts[0];
    amount.querySelector('[data-money-cents]').textContent = parts[1];
    amount.querySelector('[data-money-now]').hidden = true;
    panel.querySelector('[data-card-condition]').textContent = qualifier(offer, text);
    const base = panel.querySelector('[data-card-base]');
    base.hidden = !offer.basePrice;
    base.textContent = offer.basePrice ? text('card_base', { price: money(offer.basePrice) }) : '';
    const time = root.querySelector('.bz-product-card__updated');
    if (time) { time.dateTime = offer.checkedAt; time.textContent = text('checked', { time: window.BizedealsTime.format(offer.checkedAt) }); }
  }
  const api = { resolve, render, renderCard, discountSummary };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document !== 'undefined') {
    scope.BizedealsConditionalOffers = api;
    document.dispatchEvent(new Event('bizedeals:offers-ready'));
  }
})(typeof window === 'undefined' ? {} : window);